import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    fetchMainCategories,
    createMainCategory,
    fetchSubCategories,
    createSubCategory,
    fetchSubSubCategories,
    createSubSubCategory,
    updateSubSubCategory,
    toggleSubSubcategoriestatus
} from "./categoriesApi";

// Main categories thunks
export const fetchMainCategoriesThunk = createAsyncThunk(
    "categories/fetchMainCategories",
    async (marketType, { rejectWithValue }) => {
        try {
            const res = await fetchMainCategories(marketType);
            return [...res].sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch main categories");
        }
    }
);

export const createMainCategoryThunk = createAsyncThunk(
    "categories/createMainCategory",
    async ({ name, sortPosition, marketType }, { rejectWithValue }) => {
        try {
            const data = {
                name: name,
                slug: name,
                enable: true,
                sortPosition: sortPosition
            };
            if (marketType) {
                data.marketType = marketType;
            }
            const res = await createMainCategory(data);
            return res;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to create main category");
        }
    }
);

// Sub categories thunks
export const fetchSubCategoriesThunk = createAsyncThunk(
    "categories/fetchSubCategories",
    async (marketType, { rejectWithValue }) => {
        try {
            const res = await fetchSubCategories(marketType);
            return [...res].sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch sub categories");
        }
    }
);

export const createSubCategoryThunk = createAsyncThunk(
    "categories/createSubCategory",
    async ({ name, mainCategoryId, marketType }, { rejectWithValue }) => {
        const data = {
            name: name,
            mainCategoryId: mainCategoryId,
            enable: true
        };
        if (marketType) {
            data.marketType = marketType;
        }
        try {
            const res = await createSubCategory(data);
            return res;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to create sub category");
        }
    }
);

// Sub-sub categories thunks (existing categories)
export const fetchSubSubCategoriesThunk = createAsyncThunk(
    "categories/fetchSubSubCategories",
    async (marketType, { rejectWithValue }) => {
        try {
            const res = await fetchSubSubCategories(marketType);
            return [...res].sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch sub-sub categories");
        }
    }
);

export const createSubSubCategoryThunk = createAsyncThunk(
    "categories/createSubSubCategory",
    async ({ name, mainCategoryId, subCategoryId, marketType }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("id", "");
            formData.append("name", name);
            formData.append("slug", name);
            formData.append("mainCategoryId", mainCategoryId);
            formData.append("subCategoryId", subCategoryId);
            formData.append("image", "string");
            formData.append("chargesPercentage", 0);
            formData.append("enable", true);
            if (marketType) {
                formData.append("marketType", marketType);
            }
            const res = await createSubSubCategory(formData);
            return res;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to create sub-sub category");
        }
    }
);

export const updateSubSubCategoryThunk = createAsyncThunk(
    "categories/updateSubSubCategory",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await updateSubSubCategory(id, data);
            return res;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to update sub-sub category");
        }
    }
);

export const toggleSubSubcategoriestatusThunk = createAsyncThunk(
    "categories/toggleSubSubcategoriestatus",
    async ({ categoryId, isActive }, { rejectWithValue }) => {
        try {
            await toggleSubSubcategoriestatus(categoryId, isActive);
            return {
                id: categoryId,
                enable: isActive
            };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to toggle sub-sub category status"
            );
        }
    }
);

// Toggle all sub-sub categories under a sub category
export const toggleSubCategoryTreeStatusThunk = createAsyncThunk(
    "categories/toggleSubCategoryTreeStatus",
    async ({ subCategoryId, isActive }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const subSubItems = state.categories.subSubCategories.items || [];
            const targetIds = subSubItems
                .filter((e) => e.subCategoryId === subCategoryId)
                .map((e) => e.id);

            await Promise.all(
                targetIds.map((id) => toggleSubSubcategoriestatus(id, isActive))
            );

            return { ids: targetIds, enable: isActive };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to toggle sub category tree status"
            );
        }
    }
);

// Toggle all sub-sub categories under a main category (across all its sub categories)
export const toggleMainCategoryTreeStatusThunk = createAsyncThunk(
    "categories/toggleMainCategoryTreeStatus",
    async ({ mainCategoryId, isActive }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const subSubItems = state.categories.subSubCategories.items || [];
            const targetIds = subSubItems
                .filter((e) => e.mainCategoryId === mainCategoryId)
                .map((e) => e.id);

            await Promise.all(
                targetIds.map((id) => toggleSubSubcategoriestatus(id, isActive))
            );

            return { ids: targetIds, enable: isActive };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to toggle main category tree status"
            );
        }
    }
);
